import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LeftArrowWhite } from 'src/assets/images/svg/LeftArrow';
import { Button } from 'src/components/ui/Button';
import Preloader from 'src/components/ui/Preloader';
import { workflowStepIterator } from 'src/services/workflowStepIterator';
import { applicationStore } from 'src/store/ApplicationStore';
import logger from 'src/utils/logger';
import { parseServiceErrorMessage } from 'src/helpers/parseServiceError';
import { prepareEddResponse } from 'src/helpers/formUtils';
import { postEddForm, postPeriodicReviewForm } from 'src/services/functionServices';
import { useTranslation } from 'react-i18next';
import { getFieldLabel } from 'src/constants/fieldLabelMapping';
import { getTranslatedCountryName } from 'src/constants/countryTranslations';
import { formatAdditionalInfoValue, formatPrimaryValue } from 'src/helpers/valueFormatter';

const PreviewForm = ({ formData, handleBackToForm }) => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [submitting, setSubmitting] = useState(false);
    const currentLanguage = i18n.language || 'en';

    const formattedFormEntries = useMemo(() => {
        if (!formData) {
            return [];
        }

        return Object.entries(formData)
            .filter(([key]) => key !== 'Country' && key !== 'raw_data' && key !== 'ubos')
            .map(([key, value]) => {
                const isCountryField =
                    (key.toLowerCase().includes('country') ||
                        key.toLowerCase().includes('birthplace') ||
                        key.toLowerCase().includes('nationality')) &&
                    typeof value === 'string' &&
                    value.length === 2;

                return {
                    key,
                    label: getFieldLabel(key, currentLanguage),
                    value: formatPrimaryValue({ value, currentLanguage, isCountryField }),
                };
            });
    }, [formData, currentLanguage]);


    const handleSubmit = async () => {
        try {
            setSubmitting(true);

            const response = await workflowStepIterator({
                access_token: applicationStore.access_token,
                node_id: applicationStore.workflowNextServiceResponse.node_id,
                body: formData,
                action_index: applicationStore.workflowNextServiceResponse.index,
                constextOfStep: {
                    country: applicationStore.conditionContext?.country,
                    retry: applicationStore.workflowNextServiceResponse.retry,
                    ...(applicationStore.workflowNextServiceResponse.retry && applicationStore.lastContextOfStep),
                    ...(applicationStore.workflowNextServiceResponse.edit_form_for && {
                        edit_form_for: applicationStore.workflowNextServiceResponse.edit_form_for,
                        is_form_updated: true
                    })
                }
            });

            if (response.data?.service_results[0].status === 200) {
                applicationStore.setWorkflowNextServiceResponse(response.data);
                applicationStore.setLastContextOfStep(null);
                navigate("/next", { replace: true });
            } else {
                const rawMessage = response.data?.service_results[0]?.message;
                const parsedMessage = parseServiceErrorMessage(rawMessage);
                throw new Error(parsedMessage);
            }
        } catch (error) {
            toast.error(error?.message || "An unexpected error occurred.");
            logger.log(error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleEddSubmit = async () => {
        try {
            setSubmitting(true);
            const eddForm = prepareEddResponse(formData)
            await postEddForm(eddForm, applicationStore.aidCode, applicationStore.access_token)
            navigate("/success", { replace: true });
        } catch (error) {
            toast.error(error.message || "An unexpected error occurred.");
            logger.log(error);
        } finally {
            setSubmitting(false);
        }
    };
    const handlePeriodicSubmit = async () => {
        try {
            setSubmitting(true);
            const periodicForm = prepareEddResponse(formData)
            await postPeriodicReviewForm(periodicForm, applicationStore.aidCode, applicationStore.access_token)
            navigate("/success", { replace: true });
        } catch (error) {
            toast.error(error.message || "An unexpected error occurred.");
            logger.log(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex h-full min-h-0 w-full max-w-md mx-auto flex-col items-stretch">
            <header className="shrink-0 w-full text-left">
                <Button className="" variant="primaryBack" onClick={handleBackToForm}>
                    <LeftArrowWhite />
                </Button>
                <div className="grid gap-4 mt-6">
                    <div className="text-h6 font-bold text-flow-heading">
                        {t('clientForm.reviewTitle')}
                    </div>
                    <div className="text-body text-Gray900">
                        {t('clientForm.reviewDescription')}
                    </div>
                </div>
            </header>

            <div className="form-preview-scroll mt-4 min-h-0 flex-1 w-full min-w-0 overflow-y-auto overscroll-y-contain text-left touch-pan-y">
                <div className="flow-form-preview-panel w-full rounded-[var(--flow-radius-lg)] p-6">
                    {!formData ? (
                        <p className="text-flow font-semibold">{t('clientForm.formDataNotFound')}</p>
                    ) : (
                        <>
                            {formattedFormEntries.map(({ key: entryKey, label, value }, index) => (
                                <div key={`${entryKey}-${index}`}>
                                    {index !== 0 && <hr className="my-2 border-[color:var(--flow-border)]" />}
                                    <label className="text-flow-muted capitalize">{label}</label>
                                    <p className="mt-1 text-flow">{value}</p>
                                </div>
                            ))}

                            {formData.raw_data && formData.raw_data.length > 0 && (
                                <>
                                    <h3 className="mt-8 font-bold text-flow-heading">{t('clientForm.additionalInformation')}</h3>
                                    {formData.raw_data.map((item, index) => (
                                        <div key={`raw-${index}`}>
                                            <hr className="my-2 border-[color:var(--flow-border)]" />
                                            <label className="text-flow-muted">{getFieldLabel(item.label, currentLanguage)}</label>
                                            <div className="mt-1 max-w-full line-clamp-2 break-words overflow-hidden text-flow">
                                                {formatAdditionalInfoValue({ value: item.value, t })}
                                            </div>
                                        </div>
                                    ))}
                                </>
                            )}

                            {formData.ubos && formData.ubos.length > 0 && (
                                <>
                                    <h3 className="mt-8 font-bold text-flow-heading">
                                        {t('clientForm.keyIndividualsEntities')}
                                    </h3>
                                    {formData.ubos && formData.ubos.length > 0 && (
                                        <>
                                            {formData.ubos.map((ubo, index) => (
                                                <div key={`ubo-${index}`}>
                                                    <hr className="my-2 border-[color:var(--flow-border)]" />
                                                    <h4 className="font-bold text-flow">{t('clientForm.keyIndividualEntity', { number: index + 1 })}</h4>
                                                    {Object.entries(ubo)
                                                        .filter(([, value]) => value !== undefined && value !== null && value !== "")
                                                        .map(([key, value]) => (
                                                            <div key={key}>
                                                                <hr className="my-2 border-[color:var(--flow-border)]" />
                                                                <label className="text-flow-muted">
                                                                    {getFieldLabel(key, currentLanguage)}
                                                                </label>
                                                                <p className="mt-1 text-flow">
                                                                    {typeof value === "boolean"
                                                                        ? value ? "Yes" : "No"
                                                                        : key === "ubo_type" && typeof value === "string"
                                                                            ? value.includes("_")
                                                                                ? value
                                                                                    .split("_")
                                                                                    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                                                                    .join(" ")
                                                                                : value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
                                                                            : (key === "Country" || key === "country") && typeof value === "string" && value.length === 2
                                                                                ? getTranslatedCountryName(value, currentLanguage)
                                                                                : value}
                                                                </p>
                                                            </div>
                                                        ))}
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            <footer className="shrink-0 w-full pt-4 pb-[max(1rem,env(safe-area-inset-bottom,0px))]">
                <Button
                    className="mt-2"
                    variant="primaryWithoutIcon"
                    onClick={applicationStore.isEddForm ? handleEddSubmit : applicationStore.isPeriodicForm ? handlePeriodicSubmit : handleSubmit}
                    disabled={submitting}
                >
                    <span className="me-1">{t('clientForm.submit')}</span>{submitting && <Preloader variant='white' />}
                </Button>
                <Button
                    className="mt-2"
                    variant="secondaryWithoutIcon"
                    onClick={handleBackToForm}
                    disabled={submitting}
                >
                    {t('clientForm.backToEdit')}
                </Button>
            </footer>
        </div>
    );
};

export default PreviewForm;
